import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import { authApi } from '../services/api';
import Spinner from './Spinner';

const SetPassword: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [session, setSession] = useState('');

    useEffect(() => {
        // Get the challenge session and email from sessionStorage
        const challengeSession = sessionStorage.getItem('challengeSession');
        const challengeEmail = sessionStorage.getItem('challengeEmail');

        if (!challengeSession || !challengeEmail) {
            // If no challenge session, redirect to login
            navigate('/login');
            return;
        }

        setSession(challengeSession);
        setEmail(challengeEmail);
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        // Basic password validation
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setLoading(true);

        try {
            const response = await authApi.setPassword({
                username: email, // The API expects 'username' but we're passing the email
                newPassword,
                session,
            });

            if (response.status === 'success') {
                setSuccess('Password set successfully! Redirecting to login...');
                // Clear the challenge session data
                sessionStorage.removeItem('challengeSession');
                sessionStorage.removeItem('challengeEmail');

                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } else {
                setError(response.message || 'Failed to set password');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Clear session data and go back to login
        sessionStorage.removeItem('challengeSession');
        sessionStorage.removeItem('challengeEmail');
        navigate('/login');
    };

    if (!email || !session) {
        return null; // Will redirect in useEffect
    }

    return (
        <div
            className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='max-w-md w-full space-y-8'>
                {/* Header */}
                <div className='text-center'>
                    <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-100 mb-4'>
                        <svg
                            className='h-8 w-8 text-purple-600'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
                            ></path>
                        </svg>
                    </div>
                    <h1 className='text-4xl font-bold text-gray-900 mb-2'>Set New Password</h1>
                    <p className='text-lg text-gray-600'>
                        Welcome, <span className='font-semibold text-gray-900'>{email}</span>!
                    </p>
                    <p className='text-sm text-gray-500 mt-2'>
                        Please set a new password to continue.
                    </p>
                </div>

                {/* Password Form */}
                <div className='bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100'>
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        {/* New Password Field */}
                        <div>
                            <label
                                htmlFor='newPassword'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                New Password
                            </label>
                            <input
                                type='password'
                                id='newPassword'
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Enter new password (min 8 characters)'
                                minLength={8}
                                autoComplete='new-password'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Confirm Password Field */}
                        <div>
                            <label
                                htmlFor='confirmPassword'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Confirm Password
                            </label>
                            <input
                                type='password'
                                id='confirmPassword'
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Confirm new password'
                                minLength={8}
                                autoComplete='new-password'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                {error}
                            </div>
                        )}

                        {/* Success Message */}
                        {success && (
                            <div className='bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm'>
                                {success}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type='submit'
                            disabled={loading}
                            className='w-full bg-gradient-to-r from-blue-600 to-purple-600 !text-black font-semibold py-3 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                        >
                            {loading ? (
                                <div className='flex items-center justify-center'>
                                    <Spinner size='md' color='black' className='-ml-1 mr-3' />
                                    Setting Password...
                                </div>
                            ) : (
                                'Set Password'
                            )}
                        </button>

                        {/* Cancel Button */}
                        <button
                            type='button'
                            onClick={handleCancel}
                            disabled={loading}
                            className='w-full bg-gray-500 !text-black font-semibold py-3 px-4 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Cancel
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SetPassword;
